import { Link, useNavigate, useParams } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import { useCallback, useEffect, useState, useMemo } from "react";
import { Facility } from "../../types/Structure";
import { Facilities } from "../../api/Facilities";
import { Structures } from "../../api/Structure";
import "./FacilityEdit.css";
import Cookies from "js-cookie";
import {
  Breadcrumb,
  Card,
  Col,
  Container,
  Row,
  Button as RBButton,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Button,
  Form,
  Input,
  Select,
  TimePicker,
  Upload,
  UploadFile,
  UploadProps,
  Checkbox,
  Collapse,
  Tooltip,
} from "antd";
import { TailSpin } from "react-loader-spinner";
import { Option } from "../../models/generics";
import { Sports } from "../../api/Sports";
import { getMimeType, getSportName } from "../../utils/stringUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMinusCircle,
  faPlus,
  faSave,
  faUpload,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { FacilityType } from "../../enums/FacilityType";
import dayjs from "dayjs";

const formLayout = {
  labelCol: { xs: 24 },
  wrapperCol: { xs: 24 },
};

const weekDays = [
  { label: "Domenica", value: 0 },
  { label: "Lunedì",   value: 1 },
  { label: "Martedì",  value: 2 },
  { label: "Mercoledì",value: 3 },
  { label: "Giovedì",  value: 4 },
  { label: "Venerdì",  value: 5 },
  { label: "Sabato",   value: 6 },
];

export default function FacilityEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToasts();

  const [facilityForm] = Form.useForm();
  const [editLoading, setEditLoading] = useState(false);
  const [facility, setFacility] = useState<Facility>();
  const [sportOptions, setSportOptions] = useState<Option[]>([]);
  const [fileList, setFileList] = useState<UploadProps["fileList"]>([]);
  const [facilityUrl, setFacilityUrl] = useState<string>();
  const [structureId, setStructureId] = useState<string>();
  const [error, setError] = useState(false);

  const name      = Form.useWatch("name",      facilityForm);
  const sportId   = Form.useWatch("sportId",   facilityForm);
  const type      = Form.useWatch("type",      facilityForm);
  const timeSlots = Form.useWatch("timeSlots", facilityForm);

  /* ── Init sports ── */
  const initSports = useCallback(() => {
    Sports.getSports({}).then(
      ({ data }) => {
        setSportOptions(
          data.list.map((sport) => ({
            label: getSportName(sport.name),
            value: sport.id,
          }))
        );
      },
      () => {
        setSportOptions([]);
        showToast("sports-error", "Errore nel reperimento degli Sport", "", "danger");
      }
    );
  }, [showToast]);

  /* ── Init structure ── */
  const initStructure = useCallback(() => {
    Structures.getHostStructure().then(
      ({ data }) => setStructureId(data.structure.id),
      () => {
        setStructureId(undefined);
        Cookies.remove(window.__env.AUTH_TOKEN_NAME || "get-move-token");
        Cookies.remove(window.__env.AUTH_USER_NAME  || "get-move-user");
        window.location.href = "/";
        showToast("structure-error", "Errore nel reperimento della struttura", "", "danger");
      }
    );
  }, [showToast]);

  /* ── Retrieve facility ── */
  const retrieveFacility = useCallback(() => {
    if (!id) return;
    Facilities.getFacilityById(id).then(
      async ({ data }) => {
        const fac = data.facility as Facility;
        setFacility(fac);

        if (fac.facilityImageUrl) {
          try {
            const { data: imageData } = await Facilities.getFacilityImageUrl(fac.id!);
            setFacilityUrl(imageData.url);
            setFileList([
              {
                uid:    fac.id!,
                name:   imageData.url.split("/")[4].split("?")[0],
                status: "done",
                url:    imageData.url,
              },
            ]);
          } catch {
            showToast("facility-image-error", "Errore nel reperimento del Logo", "", "danger");
          }
        }

        const transformedTimeSlots = fac.timeSlots.map((group) => ({
          dayOfWeek: group.dayOfWeek,
          enabled:   group.enabled,
          slots:     group.slots.map((slot) => ({
            startTime:   dayjs(slot.time.split("-")[0], "HH:mm"),
            endTime:     dayjs(slot.time.split("-")[1], "HH:mm"),
            maxBookings: 1,
          })),
        }));

        facilityForm.setFieldsValue({ ...fac, timeSlots: transformedTimeSlots });
      },
      () => {
        setError(true);
        showToast("facility-error", "Errore nel reperimento del Campo", "", "danger");
      }
    );
  }, [id, facilityForm, showToast]);

  /* ── Effects ── */
  useEffect(() => {
    initSports();
    initStructure();
    if (id) {
      retrieveFacility();
    } else {
      const newFacility = new Facility();
      setFacility(newFacility);
      facilityForm.setFieldsValue({
        ...newFacility,
        timeSlots: weekDays.map((d) => ({ dayOfWeek: d.value, enabled: false, slots: [] })),
      });
    }
  }, [id, initSports, initStructure, retrieveFacility, facilityForm]);

  /* ── Upload ── */
  const handleChange: UploadProps["onChange"] = ({ file }) => {
    if (file.status === "removed") {
      setFileList([]);
    } else {
      // Con beforeUpload={() => false} antd non popola originFileObj:
      // il file grezzo (RcFile) e' gia' un File/Blob -> uso quello come fallback.
      const blob = (file.originFileObj ?? file) as unknown as Blob;
      const newFile: UploadFile = {
        uid:    file.uid,
        name:   file.name,
        status: "done",
        url:    URL.createObjectURL(blob),
      };
      setFileList([newFile]);
    }
  };

  const blobUrlToFile = async (blobUrl: string, fileName: string, mimeType: string) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new File([blob], fileName, { type: mimeType });
  };

  /* ── Submit ── */
  const submitFacility = useCallback(
    async (values: any) => {
      setEditLoading(true);
      const slots = facilityForm.getFieldValue("timeSlots");
      const formattedTimeSlots = slots.map((group: any) => ({
        dayOfWeek: group.dayOfWeek,
        enabled:   group.enabled,
        slots: (group.slots || []).map((slot: any) => ({
          time: `${dayjs(slot.startTime).format("HH:mm")}-${dayjs(slot.endTime).format("HH:mm")}`,
          maxBookings: 1,
        })),
      }));

      const fd = new FormData();
      fd.append("timeSlots", JSON.stringify(formattedTimeSlots));
      Object.entries(values)
        .filter(([key]) => key !== "timeSlots")
        .forEach(([key, value]) => fd.append(key, value + ""));

      if (fileList?.[0]?.url && fileList[0].url !== facilityUrl) {
        const file = await blobUrlToFile(
          fileList[0].url!,
          fileList[0].name,
          getMimeType(fileList[0].name)
        );
        fd.append("image", file);
      }

      if (id) {
        await Facilities.updateFacility(facility!.id!, fd)
          .then(() => {
            showToast("update-success", "Campo aggiornato con successo", "", "success");
            navigate("/gestione-campi");
          })
          .catch(() => showToast("update-error", "Errore aggiornamento Campo", "", "danger"));
      } else {
        fd.append("structureId", structureId!);
        await Facilities.createFacility(fd)
          .then(() => {
            showToast("create-success", "Campo creato con successo", "", "success");
            navigate("/gestione-campi");
          })
          .catch(() => showToast("create-error", "Errore creazione Campo", "", "danger"));
      }

      setEditLoading(false);
    },
    [fileList, facilityUrl, facility, id, structureId, navigate, showToast, facilityForm]
  );

  /* ── Disabled submit ── */
  const disabledSubmit = useMemo(() => {
    if (!id) {
      return !name || !sportId || !type || !timeSlots?.some((g: any) => g.slots?.length > 0);
    }
    if (!facility) return true;
    const isNameChanged  = name    !== facility.name;
    const isSportChanged = sportId !== facility.sportId;
    const isTypeChanged  = type    !== facility.type;
    const areSlotsChanged =
      JSON.stringify(facility.timeSlots || []) !== JSON.stringify(timeSlots || []);
    return !(isNameChanged || isSportChanged || isTypeChanged || areSlotsChanged);
  }, [id, name, sportId, type, timeSlots, facility]);

  /* ── Applica a tutti i giorni ── */
  const applyToAllDays = useCallback(
    (sourceIndex: number) => {
      const allSlots: any[] = facilityForm.getFieldValue("timeSlots") || [];
      const sourceSlots = (allSlots[sourceIndex]?.slots || []).map((s: any) => ({ ...s }));
      const updated = allSlots.map((group: any, i: number) =>
        i === sourceIndex ? group : { ...group, slots: sourceSlots }
      );
      facilityForm.setFieldsValue({ timeSlots: updated });
    },
    [facilityForm]
  );

  /* ── Time slots ── */
  const renderTimeSlots = useCallback(() => {
    const groups: any[] = facilityForm.getFieldValue("timeSlots") || [];

    return (
      <Collapse accordion size="small">
        {groups.map((group, i) => {
          const dayLabel = weekDays.find((d) => d.value === group.dayOfWeek)?.label ?? "";
          const isEnabled = facilityForm.getFieldValue(["timeSlots", i, "enabled"]);

          return (
            <Collapse.Panel
              key={i}
              collapsible={isEnabled ? "icon" : "disabled"}
              header={
                <div className="fd-day-header">
                  <Form.Item
                    name={["timeSlots", i, "enabled"]}
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox style={{ minHeight: 0, maxHeight: 32 }}>
                      <span style={{ fontWeight: 500 }}>{dayLabel}</span>
                    </Checkbox>
                  </Form.Item>
                  {isEnabled && (
                    <Tooltip title="Copia le fasce orarie di questo giorno su tutti gli altri giorni">
                      <button
                        type="button"
                        className="fd-copy-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          applyToAllDays(i);
                        }}
                      >
                        <FontAwesomeIcon icon={faCopy} className="me-1" />
                        Applica a tutti
                      </button>
                    </Tooltip>
                  )}
                </div>
              }
            >
              <Form.List name={["timeSlots", i, "slots"]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div key={key} className="fd-slot-row">
                        <Form.Item
                          {...restField}
                          name={[name, "startTime"]}
                          noStyle
                          rules={[{ required: true, message: "Inserisci ora inizio" }]}
                        >
                          <TimePicker
                            showNow={false}
                            placeholder="Inizio"
                            format="HH:mm"
                            disabled={!isEnabled}
                            size="small"
                          />
                        </Form.Item>
                        <span className="fd-slot-sep">→</span>
                        <Form.Item
                          {...restField}
                          name={[name, "endTime"]}
                          noStyle
                          rules={[{ required: true, message: "Inserisci ora fine" }]}
                        >
                          <TimePicker
                            showNow={false}
                            placeholder="Fine"
                            format="HH:mm"
                            disabled={!isEnabled}
                            size="small"
                          />
                        </Form.Item>
                        <Button
                          type="text"
                          danger
                          size="small"
                          onClick={() => remove(name)}
                          icon={<FontAwesomeIcon icon={faMinusCircle} />}
                        />
                      </div>
                    ))}

                    {isEnabled && (
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        block
                        size="small"
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        style={{ marginTop: 4 }}
                      >
                        Aggiungi fascia
                      </Button>
                    )}
                  </>
                )}
              </Form.List>
            </Collapse.Panel>
          );
        })}
      </Collapse>
    );
  }, [facilityForm, applyToAllDays]);

  /* ── Form ── */
  const renderForm = useCallback(
    () => (
      <Form form={facilityForm} {...formLayout} onFinish={submitFacility}>
        <div className="fd-container">
          <Row className="g-3">

            {/* ── Info ── */}
            <Col xs={12} md={5}>
              <div className="fd-section">
                <div className="fd-section-title">Informazioni</div>

                <Form.Item label="Nome" name="name" style={{ marginBottom: 12 }}>
                  <Input placeholder="Nome campo..." />
                </Form.Item>

                <Form.Item label="Sport" name="sportId" style={{ marginBottom: 12 }}>
                  <Select options={sportOptions} placeholder="Scegli lo sport..." />
                </Form.Item>

                <Form.Item label="Tipologia" name="type" style={{ marginBottom: 12 }}>
                  <Select
                    options={[
                      { label: "Indoor",  value: FacilityType.Indoor  },
                      { label: "Outdoor", value: FacilityType.Outdoor },
                    ]}
                    placeholder="Indoor / Outdoor..."
                  />
                </Form.Item>

                <Form.Item label="Logo" style={{ marginBottom: 0 }}>
                  <Upload
                    fileList={fileList}
                    listType="picture"
                    showUploadList={{ showDownloadIcon: true }}
                    beforeUpload={() => false}
                    onChange={handleChange}
                  >
                    <Button icon={<FontAwesomeIcon icon={faUpload} />} size="small">
                      {!fileList?.length ? "Carica logo" : "Sostituisci logo"}
                    </Button>
                  </Upload>
                </Form.Item>
              </div>
            </Col>

            {/* ── Fasce orarie ── */}
            <Col xs={12} md={7}>
              <div className="fd-section">
                <div className="fd-section-title">Fasce Orarie</div>
                {renderTimeSlots()}
              </div>
            </Col>

          </Row>
        </div>

        {/* Footer */}
        <div className="d-flex border-top p-3 justify-content-between align-items-center">
          <RBButton
            type="button"
            variant="light"
            className="border"
            onClick={() => navigate("/gestione-campi")}
            disabled={editLoading}
          >
            Annulla
          </RBButton>
          <RBButton
            type="submit"
            variant="success"
            disabled={editLoading || disabledSubmit}
          >
            {editLoading ? (
              <TailSpin color="#FFF" width={15} height={15} />
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Salva
              </>
            )}
          </RBButton>
        </div>
      </Form>
    ),
    [facilityForm, fileList, editLoading, navigate, submitFacility, renderTimeSlots, disabledSubmit, sportOptions]
  );

  /* ── Async content ── */
  const renderContent = useCallback(() => {
    if (error)
      return (
        <Alert
          type="error"
          showIcon
          message="Errore nel reperimento del Campo."
          className="m-3"
        />
      );
    return facility ? (
      renderForm()
    ) : (
      <div className="d-flex justify-content-center py-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    );
  }, [error, facility, renderForm]);

  return (
    <Container className="my-3">
      <Helmet>
        <title>{id ? "Modifica Campo" : "Nuovo Campo"}</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/gestione-campi" }}>
          Gestione Campi
        </Breadcrumb.Item>
        {id && facility && (
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/gestione-campi/${id}` }}>
            {facility.name}
          </Breadcrumb.Item>
        )}
        <Breadcrumb.Item active>{id ? "Modifica" : "Nuovo Campo"}</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <div className="fd-page-header">
          <div>
            <p className="fd-page-title">{id ? "Modifica Campo" : "Nuovo Campo"}</p>
            <p className="fd-page-subtitle">
              {id ? "Modifica i valori del campo" : "Compila i dati per creare un nuovo campo"}
            </p>
          </div>
        </div>

        {renderContent()}
      </Card>
    </Container>
  );
}
